import React, { useState, useEffect, useCallback, FormEvent } from 'react';
import { HTMLTable, Button, FileInput, ProgressBar, Collapse } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';
import * as utils from "./utils";

interface ImportMainProps {
    svc: AxiosInstance
}

interface FileInfo {
    fileName: string,
    size: number,
    type: string,
    records: number,
    error: string
}

interface TaskInfo {
    id: string,
    ref: string,
    description: string,
    startTime: string,
    endTime: string,
    userId: string,
    progressValue: number,
    progressMax: number,
    status: number,
    message: string,
    logFile: string
}

let taskOnCompleteMap = new Map()

function ImportMain({ svc }: ImportMainProps) {
    const { t, i18n } = useTranslation();
    const [fileInfoList, setFileInfoList] = useState([] as FileInfo[])
    const [file1Label, setFile1Label] = useState('choose_file' as string)
    const [file2Label, setFile2Label] = useState('choose_file' as string)
    const [file3Label, setFile3Label] = useState('choose_file' as string)
    const [file4Label, setFile4Label] = useState('choose_file' as string)
    const [file5Label, setFile5Label] = useState('choose_file' as string)
    const [taskList, setTaskList] = useState([] as TaskInfo[])
    const [showTasks, setShowTasks] = useState(false)

    const refreshImportList = useCallback(() => {
        (async () => {
            try {
                const res = await svc.post('/importlist',
                    {});
                setFileInfoList(res.data.data)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }, [svc])

    useEffect(() => {
        refreshImportList()
        /*
        (async () => {
            try {
                const res = await svc.post('/importlist',
                    {});
                setFileInfoList(res.data.data)
            } catch (error) {
                utils.showError(error)
            }
        })()
        */
    }, [svc, refreshImportList])

    useEffect(() => {
        if (taskList.findIndex((t: TaskInfo) => t.status === 0) > -1) {
            //console.log("running task(s) found, refresh tasklist in 1s")
            setTimeout(async () => {
                try {
                    const res = await svc.post('/tasklist', taskList.map((t) => t.id));
                    res.data.data.map((task: TaskInfo) => {
                        if (task.status === 1) {
                            if (taskOnCompleteMap.has(task.id)) {
                                taskOnCompleteMap.get(task.id)()
                                taskOnCompleteMap.delete(task.id)    
                            }
                        }
                    })
                    setTaskList(res.data.data)
                } catch (error) {
                    utils.showError(error)
                }
            }, 1000)
        } else {
            //console.log("all task(s) completed.")
        }
    }, [taskList, svc])

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();

        (async () => {
            try {
                const res = await svc.post('/importupload',
                    new FormData(e.target as HTMLFormElement), {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                utils.showSuccess(`${res.data.uploaded} file(s) uploaded`)
                setFileInfoList(res.data.data)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }

    const onImportFiles = () => {
        (async () => {
            try {
                const res = await svc.post('/importfiles');
                res.data.data.map((task: TaskInfo) => taskOnCompleteMap.set(task.id, () => refreshImportList()))
                setTaskList(res.data.data)
                setShowTasks(true)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }

    const onDeleteFiles = () => {
        (async () => {
            try {
                await svc.post('/importdeleteall');
                const res = await svc.post('/importlist',
                    {});
                setFileInfoList(res.data.data)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }

    return (
        <>
            <div style={{fontSize: "21px", fontWeight: 700}}>{t('import_files')}</div>
            <div className="Spacing-V-16"></div>
            <HTMLTable striped interactive condensed>
                <thead>
                    <th className="TCol-FileName">{t('file_name')}</th>
                    <th className="TCol-FileType">{t('type')}</th>
                    <th className="TCol-Records"><div className="Align-R">{t('lines')}</div></th>
                    <th className="TCol-Error">{t('error')}</th>
                </thead>
                <tbody>
                    {fileInfoList.map((f, i) =>
                        <tr key={i}>
                            <td className="TCol-FileName">{f.fileName}</td>
                            <td className="TCol-FileType">{f.type}</td>
                            <td className="TCol-Records"><div className="Align-R">{utils.formatNumber(f.records)}</div></td>
                            <td className="TCol-Error">{f.error}</td>
                        </tr>
                    )}
                </tbody>
            </HTMLTable>
            <div className="Spacing-V-8"></div>
            <div className="Row">
                <Button className="Col-1-1" icon={IconNames.IMPORT} onClick={onImportFiles}>{t('import')}</Button>
                <Button className="Col-2-1" icon={IconNames.LIST} onClick={() => setShowTasks(!showTasks)}>{t('tasks')}</Button>
                <Button className="Col-5-1" icon={IconNames.TRASH} onClick={onDeleteFiles}>{t('delete')}</Button>
            </div>
            <div className="Spacing-V-16"></div>
            <Collapse isOpen={showTasks}>
                <p>{t('tasks')}</p>
                <HTMLTable striped interactive condensed>
                    <thead>
                        <th className="TCol-Ref">{t('file_name')}</th>
                        <th className="TCol-Progress">{t('progress')}</th>
                        <th className="TCol-Message">{t('message')}</th>
                        <th className="TCol-Log">{t('log')}</th>
                    </thead>
                    <tbody>
                        {taskList.map((t, i) =>
                            <tr key={i}>
                                <td className="TCol-Ref">{t.ref}</td>
                                <td className="TCol-Progress"><ProgressBar stripes={false} animate={false} value={t.progressValue / t.progressMax} /></td>
                                <td className="TCol-Message">{t.message}</td>
                                <td className="TCol-Log">{t.logFile && <a key={i} target="_blank" rel="noopener noreferrer" href={`${svc.defaults.baseURL}/logdownload?filename=${t.logFile}&inline`}>View</a>}</td>
                            </tr>
                        )}
                    </tbody>
                </HTMLTable>
                <div className="Spacing-V-16"></div>
            </Collapse>
            <p>{t('upload_files')}</p>
            <form className="Import" onSubmit={onSubmit}>
                <div className="Row">
                    <FileInput inputProps={{ name: "file1" }} buttonText={t('browse')} text={t(file1Label)} onInputChange={utils.handleStringChange((s) => setFile1Label(utils.fileBase(s)))} />
                </div>
                <div className="Row">
                    <FileInput inputProps={{ name: "file2" }} buttonText={t('browse')} text={t(file2Label)} onInputChange={utils.handleStringChange((s) => setFile2Label(utils.fileBase(s)))} />
                </div>
                <div className="Row">
                    <FileInput inputProps={{ name: "file3" }} buttonText={t('browse')} text={t(file3Label)} onInputChange={utils.handleStringChange((s) => setFile3Label(utils.fileBase(s)))} />
                </div>
                <div className="Row">
                    <FileInput inputProps={{ name: "file4" }} buttonText={t('browse')} text={t(file4Label)} onInputChange={utils.handleStringChange((s) => setFile4Label(utils.fileBase(s)))} />
                </div>
                <div className="Row">
                    <FileInput inputProps={{ name: "file5" }} buttonText={t('browse')} text={t(file5Label)} onInputChange={utils.handleStringChange((s) => setFile5Label(utils.fileBase(s)))} />
                </div>
                <div className="Spacing-V-8"></div>
                <div className="Row">
                    <Button className="Col-1-1" type="submit" icon={IconNames.UPLOAD}>{t('upload')}</Button>
                </div>
            </form>
        </>
    )
}

export default ImportMain